import { Check } from "lucide-react";

/**
 * OnboardingShell — shared layout for both onboarding steps.
 * Includes step indicator, gradient progress bar, body, and a gradient
 * primary button in the footer.
 */
export const OnboardingShell = ({ step, total, title, subtitle, children, footer, testId }) => {
    const pct = Math.max(0, Math.min(100, (step / total) * 100));
    return (
        <div className="screen" data-testid={testId}>
            <header className="px-6 pt-[max(20px,env(safe-area-inset-top))] pb-4">
                <div className="flex items-center justify-between mb-3">
                    <span className="text-[11px] uppercase tracking-[0.16em] font-semibold text-muted-foreground">
                        Step {step} of {total}
                    </span>
                    <span className="text-[11px] font-medium text-primary" data-testid="onboarding-progress-pct">
                        {Math.round(pct)}%
                    </span>
                </div>
                <div className="h-2 rounded-full bg-secondary overflow-hidden" data-testid="onboarding-progress-bar">
                    <div
                        className="h-full rounded-full transition-[width] duration-500 ease-out"
                        style={{
                            width: `${pct}%`,
                            background:
                                "linear-gradient(90deg, hsl(var(--primary)) 0%, hsl(var(--primary) / 0.7) 100%)",
                            boxShadow: "0 0 16px hsl(var(--primary) / 0.45)",
                        }}
                    />
                </div>
                <div className="mt-5 space-y-1.5 animate-fade-up">
                    <h1 className="font-display text-[24px] leading-tight font-semibold tracking-tight">
                        {title}
                    </h1>
                    <p className="text-[13.5px] text-muted-foreground">{subtitle}</p>
                </div>
            </header>

            <div className="flex-1 overflow-y-auto no-scrollbar px-4 pb-4">{children}</div>

            <div
                className="px-6 pt-3 pb-[max(20px,env(safe-area-inset-bottom))] bg-background"
                style={{ boxShadow: "0 -16px 24px -16px hsl(var(--background))" }}
            >
                {footer}
            </div>
        </div>
    );
};

/**
 * OptionCard — premium selectable card with gradient icon, check, and glow when active.
 */
export const OptionCard = ({ icon: Icon, label, selected, onClick, testId, variant = "row" }) => {
    const isRow = variant === "row";
    return (
        <button
            type="button"
            data-testid={testId}
            aria-pressed={selected}
            onClick={onClick}
            className={[
                "group relative w-full text-left",
                "rounded-2xl border transition-[transform,box-shadow,border-color,background-color] duration-200",
                "active:scale-[0.98]",
                isRow ? "p-3.5" : "p-4",
                selected
                    ? "border-primary bg-primary/5 shadow-[0_0_0_3px_hsl(var(--primary)/0.15),0_10px_24px_-12px_hsl(var(--primary)/0.45)] scale-[1.015]"
                    : "border-border bg-card hover:bg-accent/40 shadow-soft",
            ].join(" ")}
        >
            <div className={isRow ? "flex items-center gap-3.5" : "flex flex-col items-center text-center gap-2.5"}>
                <span
                    className={[
                        "relative flex items-center justify-center shrink-0 rounded-full",
                        isRow ? "h-11 w-11" : "h-14 w-14",
                        "transition-transform duration-200",
                    ].join(" ")}
                    style={{
                        background:
                            "radial-gradient(120% 120% at 30% 20%, hsl(var(--primary) / 0.22), hsl(var(--primary) / 0.08) 60%, transparent 100%)",
                    }}
                >
                    <Icon
                        size={isRow ? 20 : 24}
                        strokeWidth={2.1}
                        className={selected ? "text-primary" : "text-primary/80 group-hover:text-primary"}
                    />
                    {selected && (
                        <span
                            className="absolute -right-1 -bottom-1 h-5 w-5 rounded-full bg-primary text-primary-foreground flex items-center justify-center shadow-card animate-fade-in"
                            aria-hidden
                        >
                            <Check size={12} strokeWidth={3} />
                        </span>
                    )}
                </span>
                <span className={isRow ? "flex-1" : ""}>
                    <span className={["block font-display font-semibold tracking-tight", isRow ? "text-[14.5px]" : "text-[13px]"].join(" ")}>
                        {label}
                    </span>
                </span>
            </div>
        </button>
    );
};

/**
 * GradientPrimaryButton — premium CTA with purple gradient + soft glow when enabled.
 */
export const GradientPrimaryButton = ({ children, onClick, disabled, testId }) => (
    <button
        type="button"
        data-testid={testId}
        onClick={onClick}
        disabled={disabled}
        className={[
            "w-full h-14 rounded-2xl font-medium text-[15px] text-primary-foreground",
            "transition-[transform,box-shadow,opacity] duration-200 active:scale-[0.98]",
            "disabled:cursor-not-allowed",
        ].join(" ")}
        style={{
            background: disabled
                ? "hsl(var(--muted))"
                : "linear-gradient(135deg, hsl(var(--primary)) 0%, hsl(var(--primary) / 0.75) 100%)",
            boxShadow: disabled
                ? "none"
                : "0 10px 28px -10px hsl(var(--primary) / 0.55), 0 0 0 1px hsl(var(--primary) / 0.25) inset",
            color: disabled ? "hsl(var(--muted-foreground))" : undefined,
        }}
    >
        {children}
    </button>
);
